import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import * as moment from 'moment-timezone';
import { StagingService } from '../../component/assigned-trips/staging/staging.service';
import { SharedDataService } from '../../service/shared-service/shared-data.service';
import { sweetAlert } from '../../utils/swal';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-staging-trip-confirm-modal',
  templateUrl: './staging-trip-confirm-modal.component.html',
  styleUrls: ['./staging-trip-confirm-modal.component.css']
})
export class StagingTripConfirmModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() trip: any;
  @Input() prePayorsList: any;
  @Input() preSpaceTypes: any;
  @Input() preCooperates: any;
  @Input() preLevelOfServices: any;
  @Output() onCloseModal = new EventEmitter<any>();
  @Output() onTripUpdated = new EventEmitter<any>();
  @Output() onOpenNotAuthorized = new EventEmitter<any>();

  user: any;
  timeZone: string = 'America/New_York';
  isLoading: boolean = false;
  isLoadingDetails: boolean = false;

  // Standing order derived state
  standingOrder: boolean = false;
  selectedDays: { [key: number]: boolean } = {};
  recurringEndDateFormatted: string = '';

  confirmChecklist: { [key: string]: boolean } = {};
  confirmFields: any[] = [];

  // Unconfirm column (opened from the confirm slideout)
  showUnconfirmColumn = false;
  unconfirmGeneralNote = '';
  unconfirmColumnError = false;

  readonly weekDays = [
    { label: 'Sun', day: 0 },
    { label: 'Mon', day: 1 },
    { label: 'Tue', day: 2 },
    { label: 'Wed', day: 3 },
    { label: 'Thu', day: 4 },
    { label: 'Fri', day: 5 },
    { label: 'Sat', day: 6 }
  ];

  constructor(
    private stagingService: StagingService,
    private sharedDataService: SharedDataService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.getUser();
    if (this.trip) {
      this.normalizeTripData();
      this.initFields();
      this.loadTripDetails();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.trip && this.trip) {
      this.normalizeTripData();
      this.initFields();
      this.loadTripDetails();
    }
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((user) => {
        if (user) {
          this.user = user;
          if (user.company && user.company.timeZone) {
            this.timeZone = user.company.timeZone;
          } else if (user.timeZone) {
            this.timeZone = user.timeZone;
          }
        }
      });
  }

  normalizeTripData() {
    if (!this.trip) return;
    const raw = this.trip;
    const idOf = (v) => (v && typeof v === 'object' ? v._id : v);

    if (!raw.jobOriginAddress && (raw.pickupAddress || (raw.pickupLocation && raw.pickupLocation.address))) {
      raw.jobOriginAddress = raw.pickupAddress || (raw.pickupLocation && raw.pickupLocation.address);
    }
    if (!raw.jobDestinationAddress && (raw.dropoffAddress || (raw.dropoffLocation && raw.dropoffLocation.address))) {
      raw.jobDestinationAddress = raw.dropoffAddress || (raw.dropoffLocation && raw.dropoffLocation.address);
    }
    if (!raw.scheduleTime && raw.pickupDateTime) {
      raw.scheduleTime = raw.pickupDateTime;
    }
    if (!raw.appointmentTime && raw.dropoffDateTime) {
      raw.appointmentTime = raw.dropoffDateTime;
    }

    // Load suggested details if exist
    const suggested = raw.suggested;
    if (suggested) {
      if (suggested.scheduleTime || suggested.pickupDateTime) {
        raw.scheduleTime = suggested.scheduleTime || suggested.pickupDateTime;
      } else if (raw.intakeSuggestedTime) {
        raw.scheduleTime = raw.intakeSuggestedTime;
      }
      if (suggested.appointmentTime || suggested.dropoffDateTime) {
        raw.appointmentTime = suggested.appointmentTime || suggested.dropoffDateTime;
      }
      if (suggested.jobOriginAddress || suggested.pickupAddress) {
        raw.jobOriginAddress = suggested.jobOriginAddress || suggested.pickupAddress;
      }
      if (suggested.jobOriginLatitude !== undefined && suggested.jobOriginLatitude !== null) {
        raw.jobOriginLatitude = suggested.jobOriginLatitude;
      }
      if (suggested.jobOriginLongitude !== undefined && suggested.jobOriginLongitude !== null) {
        raw.jobOriginLongitude = suggested.jobOriginLongitude;
      }
      if (suggested.jobDestinationAddress || suggested.dropoffAddress) {
        raw.jobDestinationAddress = suggested.jobDestinationAddress || suggested.dropoffAddress;
      }
      if (suggested.jobDestinationLatitude !== undefined && suggested.jobDestinationLatitude !== null) {
        raw.jobDestinationLatitude = suggested.jobDestinationLatitude;
      }
      if (suggested.jobDestinationLongitude !== undefined && suggested.jobDestinationLongitude !== null) {
        raw.jobDestinationLongitude = suggested.jobDestinationLongitude;
      }
      if (suggested.los) {
        raw.los = idOf(suggested.los);
      }
      if (suggested.spaceType) {
        raw.spaceType = idOf(suggested.spaceType);
      }
      if (suggested.recurringDays && suggested.recurringDays.length) {
        raw.recurringDays = suggested.recurringDays;
        raw.isStandingOrder = true;
        raw.isRecurring = true;
        raw.status = 'standing';
      }
      if (suggested.recurringEndDate || suggested.preRecurringEndDate) {
        raw.recurringEndDate = suggested.recurringEndDate || suggested.preRecurringEndDate;
      }
      if (suggested.milage !== undefined && suggested.milage !== null) {
        raw.milage = suggested.milage;
      } else if (suggested.miles !== undefined && suggested.miles !== null) {
        raw.milage = suggested.miles;
      }
    } else if (raw.intakeSuggestedTime) {
      raw.scheduleTime = raw.intakeSuggestedTime;
    }
  }

  hasSuggestedPlan(): boolean {
    if (!this.trip) return false;
    return !!(this.trip.suggested || this.trip.intakeSuggestedTime || this.trip.intakeStatus === 'suggested_plan');
  }

  computeDerivedState() {
    if (!this.trip) {
      this.standingOrder = false;
      this.selectedDays = {};
      this.recurringEndDateFormatted = '';
      return;
    }

    const t = this.trip;
    this.standingOrder = t.status === 'standing' || !!t.isRecurring || !!t.isStandingOrder || !!(t.recurringDays && t.recurringDays.length);
    const selDays: { [key: number]: boolean } = {};
    if (t.recurringDays && Array.isArray(t.recurringDays)) {
      t.recurringDays.forEach((d: any) => {
        if (!d && d !== 0) return;
        if (typeof d === 'number') {
          selDays[d] = true;
        } else if (d.checked !== false && d.day !== undefined && d.day !== null) {
          selDays[Number(d.day)] = true;
        }
      });
    }
    this.selectedDays = selDays;

    const end = t.recurringEndDate || t.preRecurringEndDate;
    this.recurringEndDateFormatted = end ? this.formatDate(end) : '';
  }

  loadTripDetails() {
    const stagingId = this.trip ? (this.trip._id || this.trip.id || this.trip.memberAssign || this.trip.tripId) : null;
    if (!stagingId) {
      return;
    }

    this.isLoadingDetails = true;
    this.stagingService.getMemberLinkRequest(stagingId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isLoadingDetails = false;
          if (res) {
            this.trip = Object.assign({}, this.trip, res);
            this.normalizeTripData();
            this.initFields(true);
          }
        },
        (err) => {
          this.isLoadingDetails = false;
          console.error('Error fetching staging trip details:', err);
        }
      );
  }

  initFields(preserveChecklist = false) {
    this.computeDerivedState();
    const prevChecklist = Object.assign({}, this.confirmChecklist);
    this.confirmFields = this.buildConfirmFields(this.trip);
    this.confirmChecklist = {};
    this.confirmFields.forEach((f) => {
      this.confirmChecklist[f.key] = preserveChecklist && prevChecklist[f.key] !== undefined ? prevChecklist[f.key] : false;
    });
    if (!preserveChecklist) {
      this.resetUnconfirmColumn();
    }
  }

  buildConfirmFields(trip: any) {
    if (!trip) return [];
    const client = trip.priorityClient || {};
    const pickupDateTime = trip.scheduleTime || trip.pickupDateTime;
    const dropoffDateTime = trip.appointmentTime || trip.dropoffDateTime;
    const spaceType = (this.preSpaceTypes && this.preSpaceTypes[trip.spaceType] && this.preSpaceTypes[trip.spaceType].title)
      || (trip.spaceTypeDetails && trip.spaceTypeDetails.title)
      || (trip.spaceType && trip.spaceType.title)
      || (trip.los && this.preLevelOfServices && this.preLevelOfServices[trip.los] && (this.preLevelOfServices[trip.los].title || this.preLevelOfServices[trip.los].name))
      || 'Standard';
    const pickupAddress = trip.jobOriginAddress || trip.pickupAddress || (trip.pickupLocation && trip.pickupLocation.address) || 'N/A';
    const dropoffAddress = trip.jobDestinationAddress || trip.dropoffAddress || (trip.dropoffLocation && trip.dropoffLocation.address) || 'N/A';

    const fields: any[] = [
      { key: 'spaceType', label: 'Space Type', value: spaceType },
      { key: 'displayName', label: 'Display Name', value: client.displayName || 'N/A' },
      {
        key: 'mobileNumber', label: 'Mobile Number', isPhone: true,
        value: client.contactNumber || client.cellPhone || client.phone || client.mobileNumber || trip.memberPhone || 'N/A'
      },
      { key: 'pickupAddress', label: 'Pickup Address', value: pickupAddress },
      {
        key: 'pickupDateTime', label: 'Pickup Date & Time', isDate: !!pickupDateTime,
        value: pickupDateTime || (trip.pickupDate ? trip.pickupDate + ' ' + (trip.pickupTime || '') : (trip.pickupTime || 'N/A'))
      },
      { key: 'dropoffAddress', label: 'Dropoff Address', value: dropoffAddress },
      {
        key: 'dropoffDateTime', label: 'Dropoff Date & Time', isDate: !!dropoffDateTime,
        value: dropoffDateTime || trip.dropoffTime || 'N/A'
      }
    ];

    if (trip.isReturn || trip.returnPickupDateTime || trip.returnAddress) {
      fields.push({
        key: 'returnTrip', label: 'Return Trip', value: trip.returnAddress || dropoffAddress,
        details: [
          { label: 'Return Pickup Date & Time', value: trip.returnPickupDateTime || 'Scheduled', isDate: !!trip.returnPickupDateTime },
          { label: 'Return Dropoff Address', value: pickupAddress },
          { label: 'Will Call', value: trip.isWillcall ? 'Yes' : 'No' }
        ]
      });
    }

    if (this.isStandingOrder()) {
      const startDate = trip.scheduleTime || trip.pickupDateTime || trip.pickupDate;
      const endDate = trip.recurringEndDate || trip.preRecurringEndDate;
      const selectedDayLabels = (this.weekDays || [])
        .filter((d: any) => this.isDaySelected(d.day))
        .map((d: any) => d.label)
        .join(', ');

      const details: any[] = [
        { label: 'Start Date', value: startDate ? this.formatDate(startDate) : 'N/A' },
        { label: 'End Date', value: endDate ? this.formatDate(endDate) : 'No end date' }
      ];

      if (selectedDayLabels) {
        details.push({ label: 'Repeat On', value: selectedDayLabels });
      }

      fields.push({
        key: 'standingOrder',
        label: 'Standing Order',
        value: selectedDayLabels ? `Repeats on ${selectedDayLabels}` : 'Standing Order Schedule',
        details
      });
    }
    return fields;
  }

  getClientId(): string {
    if (!this.trip) return '';
    const client = this.trip.client;
    if (client && typeof client === 'object') return client._id || '';
    if (client && typeof client === 'string') return client;
    const pc = this.trip.priorityClient;
    if (pc && typeof pc === 'object') return pc._id || pc.clientId || '';
    if (this.trip.clientId) return this.trip.clientId;
    return '';
  }

  getFundingSourceName(): string {
    if (!this.trip) return '';
    const coop = this.trip.cooperate;
    if (coop && typeof coop === 'object' && (coop.displayName || coop.name || coop.companyName)) {
      return coop.displayName || coop.name || coop.companyName;
    }
    if (coop && this.preCooperates && this.preCooperates[coop] && this.preCooperates[coop].displayName) {
      return this.preCooperates[coop].displayName;
    }
    if (this.trip.corporate && (this.trip.corporate.displayName || this.trip.corporate.name)) {
      return this.trip.corporate.displayName || this.trip.corporate.name;
    }
    if (coop && typeof coop === 'string' && !this.isObjectId(coop)) return coop;
    return '';
  }

  getLevelOfServiceName(): string {
    if (!this.trip) return '';
    const los = this.trip.los;
    if (los && typeof los === 'object') return los.title || los.name || '';
    if (los && this.preLevelOfServices && this.preLevelOfServices[los]) {
      return this.preLevelOfServices[los].title || this.preLevelOfServices[los].name || '';
    }
    return '';
  }

  getSpaceTypeName(): string {
    if (!this.trip) return '';
    const st = this.trip.spaceType;
    if (st && typeof st === 'object' && st.title) return st.title;
    if (st && this.preSpaceTypes && this.preSpaceTypes[st] && this.preSpaceTypes[st].title) {
      return this.preSpaceTypes[st].title;
    }
    if (st && typeof st === 'string' && !this.isObjectId(st)) return st;
    return '';
  }

  isStandingOrder(): boolean {
    return this.standingOrder;
  }

  isDaySelected(day: number): boolean {
    return !!this.selectedDays[day];
  }

  isObjectId(val: any): boolean {
    if (!val || typeof val !== 'string') return false;
    return /^[0-9a-fA-F]{24}$/.test(val);
  }

  getVerifiedCount(): number {
    return this.confirmFields.filter((f) => this.confirmChecklist[f.key]).length;
  }

  getUnverifiedFields() {
    return this.confirmFields.filter((f) => !this.confirmChecklist[f.key]);
  }

  isAllChecklistVerified(): boolean {
    if (!this.trip || !this.confirmFields.length) return false;
    return this.confirmFields.every((f) => this.confirmChecklist[f.key]);
  }

  toggleChecklistField(key: string) {
    this.confirmChecklist[key] = !this.confirmChecklist[key];
    if (this.isAllChecklistVerified()) {
      this.showUnconfirmColumn = false;
    }
  }

  toggleAllChecklist(checked: boolean) {
    this.confirmFields.forEach((f) => (this.confirmChecklist[f.key] = checked));
    if (checked) {
      this.showUnconfirmColumn = false;
    }
  }

  isUnconfirmColumnOpen(): boolean {
    return this.showUnconfirmColumn && !this.isAllChecklistVerified();
  }

  openUnconfirmColumn() {
    this.showUnconfirmColumn = true;
    this.unconfirmColumnError = false;
  }

  formatDateTime(dateStr: string): string {
    if (!dateStr) return 'N/A';
    return moment(dateStr).tz(this.timeZone).format('MM/DD/YYYY hh:mm A');
  }

  formatTime(dateStr: string): string {
    if (!dateStr) return 'N/A';
    return moment(dateStr).tz(this.timeZone).format('hh:mm A');
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return 'N/A';
    return moment(dateStr).tz(this.timeZone).format('MM/DD/YYYY');
  }

  formatConfirmFieldValue(field: any): string {
    if (field.isDate) {
      const format = this.user && this.user.dateFormat && this.user.timeFormat
        ? `${this.user.dateFormat} ${this.user.timeFormat}`
        : 'MM/DD/YYYY HHmm';
      const date = moment.utc(field.value);
      return (this.timeZone ? date.tz(this.timeZone) : date.local()).format(format);
    }
    if (field.isPhone) {
      const digits = String(field.value).replace(/\D/g, '');
      if (digits.length >= 10) {
        const d = digits.slice(-10);
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
      }
    }
    if (field.details && field.details.length) {
      const detailsStr = field.details.map((d: any) => `${d.label}: ${d.value}`).join(', ');
      return field.value ? `${field.value} (${detailsStr})` : detailsStr;
    }
    return field.value;
  }

  getUnverifiedFieldsText(): string {
    return this.getUnverifiedFields()
      .map((f) => `\u2022 ${f.label}: ${this.formatConfirmFieldValue(f)}`)
      .join('\n');
  }

  buildUnconfirmMessage(): string {
    const reason = (this.unconfirmGeneralNote || '').trim();
    return [reason, this.getUnverifiedFieldsText()].filter((part) => !!part).join('\n');
  }

  resetUnconfirmColumn() {
    this.showUnconfirmColumn = false;
    this.unconfirmGeneralNote = '';
    this.unconfirmColumnError = false;
  }

  submitUnconfirmFromChecklist() {
    if (!this.trip || this.isAllChecklistVerified()) return;
    if (!(this.unconfirmGeneralNote || '').trim()) {
      this.unconfirmColumnError = true;
      return;
    }
    const message = this.buildUnconfirmMessage();
    if (!message) {
      this.unconfirmColumnError = true;
      return;
    }

    if (this.isLoading) return;
    const tripId = this.trip._id || this.trip.id;
    this.isLoading = true;

    this.stagingService.unconfirmTrip(tripId, message)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.isLoading = false;
          this.trip.intakeStatus = 'unconfirmed';
          this.trip.intakeRejectionReason = message;
          if (data) {
            if (data.intakeStatus) this.trip.intakeStatus = data.intakeStatus;
            if (data.intakeRejectionReason) this.trip.intakeRejectionReason = data.intakeRejectionReason;
          }
          this.toastr.warning('Trip marked as not authorized', 'Not Authorized');
          this.onTripUpdated.emit({ trip: this.trip, action: 'unconfirmed' });
          this.closeModal();
        },
        (err) => {
          this.isLoading = false;
          const msg = (err && err.message) ? err.message : (err || 'Failed to unconfirm trip');
          sweetAlert('Error', msg, 'error', 'OK');
        }
      );
  }

  submitConfirmTrip() {
    if (!this.trip || !this.isAllChecklistVerified() || this.isLoading) return;
    const tripId = this.trip._id || this.trip.id;
    this.isLoading = true;

    const extraPayload: any = {};
    if (this.trip.suggested) {
      const s = this.trip.suggested;
      if (s.scheduleTime || s.pickupDateTime) extraPayload.scheduleTime = s.scheduleTime || s.pickupDateTime;
      if (s.appointmentTime || s.dropoffDateTime) extraPayload.appointmentTime = s.appointmentTime || s.dropoffDateTime;
      if (s.jobOriginAddress || s.pickupAddress) extraPayload.jobOriginAddress = s.jobOriginAddress || s.pickupAddress;
      if (s.jobOriginLatitude !== undefined && s.jobOriginLatitude !== null) extraPayload.jobOriginLatitude = s.jobOriginLatitude;
      if (s.jobOriginLongitude !== undefined && s.jobOriginLongitude !== null) extraPayload.jobOriginLongitude = s.jobOriginLongitude;
      if (s.jobDestinationAddress || s.dropoffAddress) extraPayload.jobDestinationAddress = s.jobDestinationAddress || s.dropoffAddress;
      if (s.jobDestinationLatitude !== undefined && s.jobDestinationLatitude !== null) extraPayload.jobDestinationLatitude = s.jobDestinationLatitude;
      if (s.jobDestinationLongitude !== undefined && s.jobDestinationLongitude !== null) extraPayload.jobDestinationLongitude = s.jobDestinationLongitude;
      if (s.los) extraPayload.los = typeof s.los === 'object' ? s.los._id : s.los;
      if (s.spaceType) extraPayload.spaceType = typeof s.spaceType === 'object' ? s.spaceType._id : s.spaceType;
      if (s.recurringDays && s.recurringDays.length) {
        extraPayload.recurringDays = s.recurringDays;
        extraPayload.isStandingOrder = true;
        extraPayload.isRecurring = true;
      }
      if (s.recurringEndDate || s.preRecurringEndDate) {
        extraPayload.recurringEndDate = s.recurringEndDate || s.preRecurringEndDate;
      }
      extraPayload.suggested = null;
      extraPayload.intakeSuggestedTime = null;
    } else if (this.trip.intakeSuggestedTime) {
      extraPayload.scheduleTime = this.trip.intakeSuggestedTime;
      extraPayload.intakeSuggestedTime = null;
    }

    this.stagingService.confirmTrip(tripId, extraPayload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          this.isLoading = false;
          this.trip.intakeStatus = 'confirmed';
          if (data && data.intakeStatus) {
            this.trip.intakeStatus = data.intakeStatus;
          }
          this.toastr.success('Trip confirmed successfully', 'Confirmed');
          this.onTripUpdated.emit({ trip: this.trip, action: 'confirmed' });
          this.closeModal();
        },
        (err) => {
          this.isLoading = false;
          const msg = (err && err.message) ? err.message : (err || 'Failed to confirm trip');
          sweetAlert('Error', msg, 'error', 'OK');
        }
      );
  }

  closeModal() {
    this.onCloseModal.emit(this.trip);
  }

  triggerNotAuthorized() {
    this.onOpenNotAuthorized.emit(this.trip);
  }

  ngOnDestroy(): void {}
}
